import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

import {
  MOBILE_SHEET_FOOTER_BUTTON_GAP,
  mobileSheetCancelButtonSx,
  mobileSheetConfirmButtonSx,
} from "./mobileSheetFooterButtons";

/** Altura do sheet — usado por CommonDialog e telas que abrem sobre ScreenMobile. */
export type MobileBottomSheetSize = "auto" | "medium" | "large";

export interface MobileBottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  confirmType?: "button" | "submit";
  confirmForm?: string;
  /** Soma ao z-index do modal — maior offset evita sheet atrás de Dialog fullscreen. */
  zIndexOffset?: number;
  /** Rodapé fixo (ex.: FormButtonGroup passado pelo CommonDialog). */
  footer?: ReactNode;
  size?: MobileBottomSheetSize;
}

const SHEET_MAX_HEIGHT: Record<MobileBottomSheetSize, string> = {
  auto: "min(88dvh, calc(100dvh - env(safe-area-inset-top, 0px)))",
  medium: "min(58dvh, calc(100dvh - env(safe-area-inset-top, 0px)))",
  large: "min(92dvh, calc(100dvh - env(safe-area-inset-top, 0px)))",
};

const SHEET_MIN_HEIGHT: Partial<Record<MobileBottomSheetSize, string>> = {
  medium: "42dvh",
  large: "72dvh",
};

const FOOTER_SAFE_PADDING = "calc(16px + env(safe-area-inset-bottom, 0px))";

export const MOBILE_BOTTOM_SHEET_Z_INDEX_OFFSET = 20;

export const selectMenuAboveMobileBottomSheetProps = {
  sx: {
    zIndex: (theme: { zIndex: { modal: number } }) =>
      theme.zIndex.modal + MOBILE_BOTTOM_SHEET_Z_INDEX_OFFSET + 1,
  },
};

export function MobileBottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  onConfirm,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  confirmDisabled = false,
  confirmType = "button",
  confirmForm,
  zIndexOffset = MOBILE_BOTTOM_SHEET_Z_INDEX_OFFSET,
  footer,
  size = "auto",
}: MobileBottomSheetProps) {
  const theme = useTheme();
  const hasCustomFooter = Boolean(footer);
  const hasBuiltinFooter = Boolean(onConfirm || confirmForm);
  const showFooter = hasCustomFooter || hasBuiltinFooter;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      swipeAreaWidth={0}
      ModalProps={{
        keepMounted: false,
        sx: { zIndex: theme.zIndex.modal + zIndexOffset },
        BackdropProps: {
          sx: { bgcolor: alpha(theme.palette.common.black, 0.45) },
        },
      }}
      PaperProps={{
        sx: {
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "20px 20px 0 0",
          maxHeight: SHEET_MAX_HEIGHT[size],
          minHeight: SHEET_MIN_HEIGHT[size],
          bgcolor: "background.paper",
          boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.14)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 99,
            bgcolor: "divider",
          }}
        />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          pb: 1,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label="Fechar"
          onClick={onClose}
          sx={{ flexShrink: 0 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          py: 1.5,
        }}
      >
        {children}
      </Box>

      {showFooter ? (
        hasCustomFooter ? (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1,
              pb: FOOTER_SAFE_PADDING,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {footer}
          </Box>
        ) : (
          <Stack
            direction="row"
            spacing={MOBILE_SHEET_FOOTER_BUTTON_GAP}
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1,
              pb: FOOTER_SAFE_PADDING,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={mobileSheetCancelButtonSx}
            >
              {cancelLabel}
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              type={confirmType}
              form={confirmForm}
              disabled={confirmDisabled}
              onClick={confirmType === "button" ? onConfirm : undefined}
              sx={mobileSheetConfirmButtonSx}
            >
              {confirmLabel}
            </Button>
          </Stack>
        )
      ) : null}
    </SwipeableDrawer>
  );
}
